import { PackageProviderIcon } from 'components/shared/ppoints/PackageProviderIcon';
import { ShippingRateSelector } from 'components/shared/ppoints/ShippingRateSelector';
import { SCard } from 'components/shared/s-polaris';
import {
    MarketRateCountryGroup,
    MarketRateCountryProviderEntry,
} from 'features/shipping-provider/settings/hooks/useMarketRateCountryGroups';
import { useMarketRateOptions } from 'features/shipping-provider/settings/hooks/useMarketRateOptions';
import { useGetCountryName } from 'hooks/ppoints/useGetCountryName';
import { useTranslation } from 'react-i18next';
import PackageProviderService from 'services/settings/ppoints/PackageProviderService';

const MarketRateCountryProviderRow: React.FC<{ entry: MarketRateCountryProviderEntry }> = ({ entry }) => {
    const { options, selectedOptions } = useMarketRateOptions(entry.market, entry.values, entry.rateOptions);

    return (
        <div className="flex justify-between items-center gap-10">
            <div className="flex gap-2 items-center">
                <PackageProviderIcon provider={entry.provider} size="small" />
                <s-heading>{PackageProviderService.getDisplayName(entry.provider)}</s-heading>
            </div>
            <div className="w-full max-w-100">
                <ShippingRateSelector
                    options={options}
                    values={entry.values}
                    selectedOptions={selectedOptions}
                    markets={entry.market.markets}
                    hasError={entry.hasOptionsError}
                    onChange={entry.onChange}
                    onRatesUpdated={entry.onRatesUpdated}
                />
            </div>
        </div>
    );
};

/**
 * One country's rate mapping for every provider active in it — same card shape as
 * `RateForProviderSelector`'s zone card (`SCard` + zone-style heading, one row per provider,
 * `s-divider` between rows), just keyed on a country from `useMarketRateCountryGroups` instead of a
 * Shopify shipping zone.
 */
const MarketRateCountryCard: React.FC<{ group: MarketRateCountryGroup }> = ({ group }) => {
    const { t } = useTranslation();
    const { getTranslatedCountryName } = useGetCountryName();

    return (
        <SCard>
            <s-heading>
                {t('WIZARD.MARKET_DRIVEN_SHIPPING_STEP.RATES_HEADING', {
                    country: getTranslatedCountryName(group.countryCode),
                })}
            </s-heading>
            <div className="flex flex-col gap-2 py-3">
                {group.providers.map((entry, index) => (
                    <div key={entry.provider}>
                        <MarketRateCountryProviderRow entry={entry} />
                        {group.providers.length - 1 !== index && (
                            <div className="pt-2">
                                <s-divider />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </SCard>
    );
};

export default MarketRateCountryCard;
